import React from 'react'
import { Box, Typography, Grid, Chip } from '@mui/material'

interface ProviderData {
  today: number
  thisMonth: number
  errors: { today: number; thisMonth: number }
}

interface QuotaData {
  gemini: ProviderData
  openai: ProviderData
  rapidapi: ProviderData
  xpoz: ProviderData
}

interface ErrorEntry {
  provider: string
  message: string
  statusCode: number | null
  timestamp: string
}

interface ApiQuotaCardsProps {
  quota: QuotaData | null
  recentErrors: ErrorEntry[]
}

const providerConfig: Record<string, { label: string; color: string }> = {
  gemini: { label: 'Gemini', color: '#4285f4' },
  openai: { label: 'OpenAI', color: '#10a37f' },
  rapidapi: { label: 'RapidAPI', color: '#0055da' },
  xpoz: { label: 'Xpoz', color: '#f97316' },
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return `${Math.floor(diffHours / 24)}d ago`
}

export default function ApiQuotaCards({ quota, recentErrors }: ApiQuotaCardsProps): JSX.Element {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '16px',
        }}
      >
        API Usage (since last restart)
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(providerConfig).map(([key, config]) => {
          const data = quota?.[key as keyof QuotaData]
          const errorsToday = data?.errors?.today ?? 0
          const errorsMonth = data?.errors?.thisMonth ?? 0
          return (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <Box
                sx={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '12px',
                  padding: '24px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: config.color,
                    marginBottom: '16px',
                  }}
                >
                  {config.label}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{ fontSize: '24px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}
                    >
                      {data?.today ?? 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.5)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Today
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{ fontSize: '24px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}
                    >
                      {data?.thisMonth ?? 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.5)',
                        textTransform: 'uppercase',
                      }}
                    >
                      This Month
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    borderTop: '1px solid rgba(239, 68, 68, 0.15)',
                    paddingTop: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: errorsToday > 0 ? '#ef4444' : 'rgba(255,255,255,0.3)',
                      }}
                    >
                      {errorsToday}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: errorsToday > 0 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Errors Today
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: errorsMonth > 0 ? '#ef4444' : 'rgba(255,255,255,0.3)',
                      }}
                    >
                      {errorsMonth}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: errorsMonth > 0 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Errors Month
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )
        })}
      </Grid>

      <Box
        sx={{
          marginTop: '16px',
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(24px)',
          border: `1px solid ${recentErrors.length > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(168, 85, 247, 0.2)'}`,
          borderRadius: '12px',
          padding: '20px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: recentErrors.length > 0 ? '#ef4444' : 'rgba(255,255,255,0.5)',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Recent Errors ({recentErrors.length})
        </Typography>
        {recentErrors.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
            No errors since last restart
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflow: 'auto' }}>
            {recentErrors.map((err, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: 'rgba(239, 68, 68, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(239, 68, 68, 0.1)',
                }}
              >
                <Chip
                  label={err.provider}
                  size="small"
                  sx={{
                    fontSize: '10px',
                    height: '20px',
                    backgroundColor: providerConfig[err.provider]?.color ?? '#666',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
                {err.statusCode != null && (
                  <Chip
                    label={err.statusCode}
                    size="small"
                    sx={{
                      fontSize: '10px',
                      height: '20px',
                      backgroundColor: err.statusCode === 429 ? '#f59e0b' : '#ef4444',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.7)',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {err.message}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.4)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatTime(err.timestamp)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
